import { useEffect, useRef, useCallback, useState } from 'react';

import { getBrowserLocation, getWebSocketConstructor, type WebSocketLike } from './browser';

export type SensorEventType = 'registered' | 'unregistered' | 'value_changed' | 'config_changed';

export interface SensorStreamEvent {
  type: SensorEventType;
  sensorId: string;
  humanId?: string;
  name?: string;
  value?: number;
  oldValue?: number;
  unit?: string;
  provider?: string;
  timestamp: string;
}

export interface UseSensorStreamOptions {
  /**
   * Filter pattern for sensor events (optional, omit for all sensors).
   * Supports:
   * - Single sensor: "temp-living-room"
   * - Multiple sensors: "temp-living-room,temp-bedroom"
   * - Wildcards: "hue-*", "*-temperature", "*power*"
   */
  filter?: string;
  /** Whether to connect automatically (default: true) */
  enabled?: boolean;
  /** Callback when a sensor event is received */
  onEvent?: (event: SensorStreamEvent) => void;
  /** Reconnection delay in ms (default: 3000) */
  reconnectDelay?: number;
}

export interface UseSensorStreamResult {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Most recent event received */
  lastEvent: SensorStreamEvent | null;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

/**
 * Hook to subscribe to real-time sensor updates via WebSocket
 *
 * @param options - Configuration options
 * @returns Connection state and controls
 */
export function useSensorStream(options: UseSensorStreamOptions = {}): UseSensorStreamResult {
  const { filter, enabled = true, onEvent, reconnectDelay = 3000 } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<SensorStreamEvent | null>(null);

  const wsRef = useRef<WebSocketLike | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const onEventRef = useRef(onEvent);

  // Keep callback ref updated
  onEventRef.current = onEvent;

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [clearReconnectTimeout]);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Build WebSocket URL
    const location = getBrowserLocation();
    const WebSocketImpl = getWebSocketConstructor();
    if (!location || !WebSocketImpl) return;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = location.host;
    const params = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    const url = `${protocol}//${host}/api/sensors/stream${params}`;

    const ws = new WebSocketImpl(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) {
        setIsConnected(true);
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setIsConnected(false);
        // Schedule reconnection
        if (enabled) {
          clearReconnectTimeout();
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        }
      }
    };

    ws.onerror = () => {
      // Error will be followed by close, so we don't need to do much here
      ws.close();
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;

      try {
        const data = JSON.parse(event.data) as SensorStreamEvent;
        setLastEvent(data);
        onEventRef.current?.(data);
      } catch {
        // Ignore malformed messages
      }
    };
  }, [enabled, filter, reconnectDelay, clearReconnectTimeout]);

  const reconnect = useCallback(() => {
    disconnect();
    connect();
  }, [disconnect, connect]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, filter, connect, disconnect]);

  return {
    isConnected,
    lastEvent,
    reconnect,
    disconnect,
  };
}
